export const PLAYER_MODE = {
  CLASSIC: 'classic',
  WAVEFORM: 'waveform',
  DETAILED: 'detailed',
} as const;

export type PlayerMode = (typeof PLAYER_MODE)[keyof typeof PLAYER_MODE];

export type AudioPlayerOptions = {
  /** Player visualization mode */
  playerMode?: PlayerMode;
  /** Show episode thumbnail image */
  showThumbnail?: boolean;
  /** Show episode title */
  showTitle?: boolean;
  /** Show rewind button */
  showRewind?: boolean;
  /** Show forward button */
  showForward?: boolean;
  /** Rewind interval in seconds */
  rewindSeconds?: number;
  /** Forward interval in seconds */
  forwardSeconds?: number;
  /** Primary color (progress fill, waveform progress, icon color) */
  primaryColor?: string;
  /** Secondary color (waveform unfilled) */
  secondaryColor?: string;
  /** Player background color */
  backgroundColor?: string;
  /** Text color for title and time labels */
  textColor?: string;
  /** Progress bar track color (classic mode) */
  trackColor?: string;
  /** Icon color override (defaults to textColor or primaryColor) */
  iconColor?: string;
  /** Play/pause button size in px */
  playButtonSize?: number;
  /** Skip button icon size in px */
  skipButtonSize?: number;
  /** Waveform bar height in px */
  waveformHeight?: number;
  /** Progress bar height in px */
  progressBarHeight?: number;
};

export type AudioPlayerProps = {
  subtitle?: string;
  /** Audio source URL */
  audioUrl: string;
  /** Audio MIME type */
  audioType?: string;
  /** Known duration in seconds (from API), used to enable playback before metadata loads */
  duration?: number;
  /** Episode thumbnail URL */
  thumbnailUrl?: string;
  /** Episode title */
  title?: string;
  /** Compact waveform payload from backend (u8-base64 encoded peaks) */
  waveform?: unknown;
  /** Player configuration options (merged with theme preset defaults) */
  options?: AudioPlayerOptions;
  /** Whether this is a live stream */
  isLive?: boolean;
  /** Additional CSS class */
  className?: string;
  /** Called instead of default play behavior when provided (e.g. to gate playback behind auth) */
  onPlayAttempt?: () => void;
  /** Tooltip text if present */
  tooltipText?: string;
  /** Whether playback is locked */
  isPlaybackLocked?: boolean;
  /** Show a Preview badge (gated episode preview clip) */
  isPreview?: boolean;
  /** HTML id for the audio element, used by transcript to find this specific player */
  audioElementId?: string;
  /** localStorage key for persisting/restoring playback position */
  playbackStorageKey?: string;
};

/** Props passed to internal player components */
export type PlayerControlProps = {
  audioUrl: string;
  audioType?: string;
  /** Known duration in seconds (from API), used to enable playback before metadata loads */
  duration?: number;
  thumbnailUrl?: string;
  title?: string;
  /** Compact waveform payload from backend (u8-base64 encoded peaks) */
  waveform?: unknown;
  isLive?: boolean;
  /** Called instead of default play behavior when provided */
  onPlayAttempt?: () => void;
  /** Whether playback is locked */
  isPlaybackLocked?: boolean;
  /** Show a Preview badge (gated episode preview clip) */
  isPreview?: boolean;
  /** HTML id for the audio element */
  audioElementId?: string;
  /** localStorage key for persisting/restoring playback position */
  playbackStorageKey?: string;
} & Required<AudioPlayerOptions>;

/** TipTap serializer node shape */
export type TAudioPlayerElement = {
  type: 'audioPlayer';
  attrs?: {
    id?: string;
    mergeTag?: string;
    data?: {
      audioUrl?: string;
      audioType?: string;
      thumbnailUrl?: string;
      title?: string;
      waveform?: unknown;
      youtubeVideoId?: string;
      duration?: number;
      isPreview?: boolean;
    };
    isLive?: boolean;
  } & AudioPlayerOptions;
};
